import React, { useCallback } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { useRouterActions } from 'react-resource-router';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Tooltip, { PositionType } from '@atlaskit/tooltip';

import {
  ConnectBranchSummaryActions,
  ConnectBranchSummaryInfo,
} from 'src/connect/branch-modules';
import { useIntl } from 'src/hooks/intl';
import { openMergeBranchDialog } from 'src/redux/branches';
import { openDeleteBranchDialog } from 'src/redux/branches/actions/delete-branch-dialog';
import { getConflictStatus } from 'src/redux/pull-request/selectors';
import { getCurrentRepoCommits } from 'src/redux/repo-commits/selectors';
import {
  getBranchViewDestinationPermissions,
  getBranchViewSourceBranchHash,
  getBranchViewSourceBranchIsMainBranch,
  getBranchViewSourceBranchPermissions,
  getBranchViewDestinationBranchName,
  getBranchViewDestinationRepoName,
  getBranchViewSourceBranchName,
} from 'src/redux/repository/branch/branch-redux';
import { Link } from 'src/router/components';
import CheckoutButton from 'src/sections/repository/components/checkout-button';
import RepositoryAccessGuard from 'src/sections/repository/components/repository-access-guard';
import actionMessages from 'src/sections/repository/sections/branches/components/branch-table/branch-actions.i18n';
import { DiffSettingsButton } from 'src/sections/repository/sections/pull-request/components/diff-settings/button';
import urls from 'src/sections/repository/urls';
import {
  getCurrentRepository,
  getCurrentRepositoryOwnerName,
  getCurrentRepositorySlug,
} from 'src/selectors/repository-selectors';
import { ui } from 'src/urls/source/ui';
import { publishUiEvent } from 'src/utils/analytics/publish';

import {
  BranchPrListResponse,
  openPullRequestExists,
} from '../../hooks/branch-pr-list';
import { messages as branchMessages } from '../branch-page.i18n';

import * as styles from './header-actions.style';

type Props = {
  branchName: string;
  branchPrList: BranchPrListResponse;
};

const DisabledButtonTooltip = ({
  children,
  message,
  location = 'left',
}: {
  children: JSX.Element;
  message: string;
  location?: PositionType;
}): JSX.Element => {
  return (
    <Tooltip content={message} position={location} testId="test-header-tooltip">
      {children}
    </Tooltip>
  );
};

export const HeaderActions = ({
  branchName,
  branchPrList,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const repository = useSelector(getCurrentRepository);
  const sourceHash = useSelector(getBranchViewSourceBranchHash) || '';
  const destRepoName = useSelector(getBranchViewDestinationRepoName) || '';
  const destBranchName = useSelector(getBranchViewDestinationBranchName) || '';

  const sourcePath =
    repository &&
    ui.source(repository.full_name, {
      refOrHash: sourceHash || branchName,
      at: branchName,
    });

  const ownerString =
    repository && repository.workspace ? repository.workspace.slug || '' : '';
  const createPrPath =
    (repository &&
      `${urls.ui.createPullRequest(
        ownerString,
        repository.slug || '',
        branchName,
        destRepoName,
        destBranchName
      )}&event_source=branch_detail`) ||
    '';

  const publishBranchLinkClick = (linkId: string) => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: linkId,
      source: 'branchViewPage',
    });
  };

  const intl = useIntl();

  const { push } = useRouterActions();

  const slug = useSelector(getCurrentRepositorySlug);
  const owner = useSelector(getCurrentRepositoryOwnerName);
  const sourceBranchName = useSelector(getBranchViewSourceBranchName);
  const fullSlug = `${owner}/${slug}`;
  const hasMergeConflicts = useSelector(getConflictStatus);
  const currentRepoCommits = useSelector(getCurrentRepoCommits);

  const sourceBranchIsMainBranch = useSelector(
    getBranchViewSourceBranchIsMainBranch
  );
  const sourceBranchPermissions = useSelector(
    getBranchViewSourceBranchPermissions
  );
  const destBranchPermissions = useSelector(
    getBranchViewDestinationPermissions
  );

  const hasOpenPullRequest = openPullRequestExists(branchPrList);

  const renderPRButton = useCallback(() => {
    const commits = currentRepoCommits || [];
    const hasNoCommits = commits && commits.length === 0;
    const canCreatePR = !hasNoCommits;

    const prButton = (
      <styles.CreatePRWrapper>
        <Button
          onClick={() => {
            publishBranchLinkClick('createPR');
            push(createPrPath);
          }}
          testId="createPR"
          isDisabled={hasOpenPullRequest}
        >
          {intl.formatMessage(branchMessages.createPR)}
        </Button>
      </styles.CreatePRWrapper>
    );

    const buttonWithTooltip = (
      <Tooltip
        content={intl.formatMessage(branchMessages.pullRequestExists)}
        position="bottom"
      >
        {prButton}
      </Tooltip>
    );

    return canCreatePR
      ? hasOpenPullRequest
        ? buttonWithTooltip
        : prButton
      : null;
  }, [currentRepoCommits, intl, createPrPath, hasOpenPullRequest, push]);

  const renderMergeButton = useCallback(() => {
    const shouldDisable =
      hasMergeConflicts || destBranchPermissions?.push !== 'allow';

    const MergeButton = (): JSX.Element => {
      return (
        <DropdownItem
          isDisabled={shouldDisable}
          onClick={() => {
            publishBranchLinkClick('mergeBranch');
            if (sourceBranchName && destBranchName && destRepoName) {
              dispatch(
                openMergeBranchDialog({
                  sourceBranchName,
                  sourceRepositoryFullName: fullSlug,
                  destinationBranchName: destBranchName,
                  destinationRepositoryFullName: destRepoName,
                  renderFlagOnSuccess: false,
                  canDeleteSourceBranch:
                    sourceBranchPermissions?.delete === 'allow' || false,
                })
              );
            }
          }}
          testId="mergeButton"
        >
          {intl.formatMessage(branchMessages.merge)}
        </DropdownItem>
      );
    };

    const renderDisabledMergeButton = (tooltipMessage: string) => {
      return (
        <DisabledButtonTooltip message={tooltipMessage}>
          <span>
            <MergeButton />
          </span>
        </DisabledButtonTooltip>
      );
    };

    if (hasMergeConflicts) {
      return renderDisabledMergeButton(
        intl.formatMessage(branchMessages.mergeConflict)
      );
    }
    if (destBranchPermissions?.push !== 'allow') {
      return renderDisabledMergeButton(
        intl.formatMessage(actionMessages.mergeDisabled, {
          destination: destBranchName,
        })
      );
    }
    return <MergeButton />;
  }, [
    dispatch,
    hasMergeConflicts,
    destBranchPermissions,
    destBranchName,
    intl,
    destRepoName,
    fullSlug,
    sourceBranchName,
    sourceBranchPermissions,
  ]);

  const renderDeleteButton = useCallback(() => {
    const shouldDisable =
      sourceBranchIsMainBranch || sourceBranchPermissions?.delete !== 'allow';

    const DeleteButton = (): JSX.Element => {
      return (
        <DropdownItem
          isDisabled={shouldDisable}
          onClick={() => {
            publishBranchLinkClick('deleteBranch');
            dispatch(openDeleteBranchDialog(branchName));
          }}
        >
          {intl.formatMessage(branchMessages.delete)}
        </DropdownItem>
      );
    };

    const renderDisabledDeleteButton = (tooltipMessage: string) => {
      return (
        <DisabledButtonTooltip message={tooltipMessage}>
          <span>
            <DeleteButton />
          </span>
        </DisabledButtonTooltip>
      );
    };

    if (sourceBranchIsMainBranch) {
      return renderDisabledDeleteButton(
        intl.formatMessage(actionMessages.deleteDisabledMainBranch)
      );
    }
    if (sourceBranchPermissions?.delete !== 'allow') {
      return renderDisabledDeleteButton(
        intl.formatMessage(actionMessages.deleteDisabledPermissions)
      );
    }
    return <DeleteButton />;
  }, [
    sourceBranchIsMainBranch,
    sourceBranchPermissions,
    branchName,
    dispatch,
    intl,
  ]);

  return (
    <styles.HeaderActions>
      <styles.HeaderActionsButtonWrapper>
        <ButtonGroup>
          {repository && (
            <CheckoutButton
              onClick={() => publishBranchLinkClick('checkout')}
              refName={branchName}
              repository={repository}
            />
          )}
          <Button
            href={sourcePath}
            component={Link}
            onClick={() => publishBranchLinkClick('viewSource')}
          >
            {intl.formatMessage(branchMessages.source)}
          </Button>
          <RepositoryAccessGuard requiredLevel="write">
            {renderPRButton()}
            <DropdownMenu
              spacing="compact"
              trigger={({ triggerRef, ...props }) => (
                <Button
                  {...props}
                  iconBefore={<MoreIcon label="more" />}
                  ref={triggerRef}
                />
              )}
              testId="branch-more-button"
            >
              <DropdownItemGroup>
                {renderMergeButton()}
                {renderDeleteButton()}
                <ConnectBranchSummaryInfo
                  name={branchName}
                  component={DropdownItem}
                />
                <ConnectBranchSummaryActions
                  name={branchName}
                  component={DropdownItem}
                />
              </DropdownItemGroup>
            </DropdownMenu>
          </RepositoryAccessGuard>
          <DiffSettingsButton />
        </ButtonGroup>
      </styles.HeaderActionsButtonWrapper>
    </styles.HeaderActions>
  );
};
